/**
 * §5.6's web scroll-anchoring mechanism: auto-scroll-to-bottom when caught
 * up, the "new messages" pill trigger, and prepend-anchor-preservation for
 * "load older" (ISSUE-280 plan WI-23).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";

export const AT_LATEST_THRESHOLD_PX = 48;
export const LOAD_OLDER_THRESHOLD_PX = 120;

/** How long a jumped-to row stays flagged `data-highlighted="true"` before it clears (WI-W13). */
export const JUMP_HIGHLIGHT_MS = 1500;

export function useConversationScroll(input: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  rowCount: number;
  isPrependingOlder: boolean;
}): {
  isAtLatest: boolean;
  newMessagesBelowCount: number;
  onScroll: () => void;
  onNewMessagesArrived: (wasAtLatestBeforeUpdate: boolean) => void;
  scrollToLatest: () => void;
  stickToLatest: () => void;
  jumpToMessage: (messageId: string) => boolean;
} {
  const [isAtLatest, setIsAtLatest] = useState(true);
  const [newMessagesBelowCount, setNewMessagesBelowCount] = useState(0);
  const previousScrollHeightRef = useRef(0);
  const previousScrollTopRef = useRef(0);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightedElementRef = useRef<HTMLElement | null>(null);
  // Set when new rows arrive while the fan is caught up. The rows are not in
  // the DOM yet at that moment, so scrolling then lands on the OLD bottom and
  // a tall arrival (a game card) is left cut off below (QA round 1). The
  // layout effect below finishes the scroll once the rows are rendered.
  const isStickPendingRef = useRef(false);
  const stickFrameRef = useRef<number | null>(null);
  // Mirrors `isAtLatest` for the image load listener below, which is bound
  // once and must read the latest value, not the one from its first render.
  const isAtLatestRef = useRef(true);

  const onScroll = (): void => {
    const container = input.containerRef.current;
    if (!container) return;
    const atLatest =
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      AT_LATEST_THRESHOLD_PX;
    setIsAtLatest(atLatest);
    isAtLatestRef.current = atLatest;
    if (atLatest) setNewMessagesBelowCount(0);
    // The fan scrolled up to read: a stick still waiting must not yank them.
    else isStickPendingRef.current = false;
  };

  const scrollToLatest = (): void => {
    const container = input.containerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
    setNewMessagesBelowCount(0);
  };

  /**
   * SOCIAL-NEW-1 (QA round 3). Takes the fan to the newest message whatever
   * they were looking at: when a conversation opens, and when they send a
   * message of their own. Rows staged in the same commit are not in the DOM
   * yet, so the layout effect below finishes the scroll once they render.
   */
  const stickToLatest = (): void => {
    const container = input.containerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
    isStickPendingRef.current = true;
    isAtLatestRef.current = true;
    setIsAtLatest(true);
    setNewMessagesBelowCount(0);
  };

  const onNewMessagesArrived = (wasAtLatestBeforeUpdate: boolean): void => {
    if (wasAtLatestBeforeUpdate) {
      const container = input.containerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
      isStickPendingRef.current = true;
      setNewMessagesBelowCount(0);
    } else {
      isStickPendingRef.current = false;
      setNewMessagesBelowCount((prev) => prev + 1);
    }
  };

  // Captures the scroll anchor immediately before a "load older" prepend
  // lands, so the fan's current read position does not shift.
  useLayoutEffect(() => {
    const container = input.containerRef.current;
    if (!container) return;

    if (input.isPrependingOlder) {
      container.scrollTop =
        container.scrollHeight -
        previousScrollHeightRef.current +
        previousScrollTopRef.current;
    }

    if (isStickPendingRef.current && !input.isPrependingOlder) {
      isStickPendingRef.current = false;
      container.scrollTop = container.scrollHeight;
      // A card's team marks and photos can still grow the row after this
      // paint, so take the bottom once more on the next frame.
      if (stickFrameRef.current !== null) {
        cancelAnimationFrame(stickFrameRef.current);
      }
      stickFrameRef.current = requestAnimationFrame(() => {
        stickFrameRef.current = null;
        const current = input.containerRef.current;
        if (current) current.scrollTop = current.scrollHeight;
      });
    }

    previousScrollHeightRef.current = container.scrollHeight;
    previousScrollTopRef.current = container.scrollTop;
  }, [input.rowCount]);

  // No new hook input for this: the target row is found by data attribute
  // directly inside the scroll container rather than by threading the
  // current message list into this hook (RF-1 adds no new input to it).
  // Returns true when the row was found and highlighted, false when it is
  // not in the DOM (ISSUE-1054).
  const jumpToMessage = (messageId: string): boolean => {
    const container = input.containerRef.current;
    if (!container) return false;

    if (highlightTimerRef.current !== null) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    if (highlightedElementRef.current) {
      highlightedElementRef.current.removeAttribute("data-highlighted");
      highlightedElementRef.current = null;
    }

    // `messageId` comes straight off the URL (`?messageId=`), so a
    // hand-edited value containing a `"` (or anything else that is not a
    // valid CSS attribute-selector value) makes this selector invalid. A
    // throw here would happen synchronously inside a `useEffect` with
    // nothing to catch it, so treat that the same as "not currently
    // loaded" rather than letting it escape.
    let target: HTMLElement | null = null;
    try {
      target = container.querySelector<HTMLElement>(
        `[data-message-id="${messageId}"]`,
      );
    } catch {
      target = null;
    }

    // Not in the DOM. The caller decides whether to page back
    // (ConversationPane, ISSUE-1054). Scroll position is left alone.
    if (!target) {
      return false;
    }

    target.scrollIntoView({ block: "center" });
    target.setAttribute("data-highlighted", "true");
    highlightedElementRef.current = target;
    highlightTimerRef.current = setTimeout(() => {
      target.removeAttribute("data-highlighted");
      highlightedElementRef.current = null;
      highlightTimerRef.current = null;
    }, JUMP_HIGHLIGHT_MS);
    return true;
  };

  // SOCIAL-NEW-1. Photos, GIFs, team marks and link previews finish loading
  // after the rows render and push the newest message back below the fold.
  // While the fan is caught up the list follows them down; once they scroll
  // up to read history it leaves them where they are. `load` does not bubble,
  // so the listener sits in the capture phase.
  useEffect(() => {
    const container = input.containerRef.current;
    if (!container || typeof container.addEventListener !== "function") {
      return;
    }
    const onContentLoaded = (): void => {
      if (!isAtLatestRef.current) return;
      container.scrollTop = container.scrollHeight;
    };
    container.addEventListener("load", onContentLoaded, true);
    return () => {
      container.removeEventListener("load", onContentLoaded, true);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current !== null) {
        clearTimeout(highlightTimerRef.current);
      }
      if (stickFrameRef.current !== null) {
        cancelAnimationFrame(stickFrameRef.current);
      }
    };
  }, []);

  return {
    isAtLatest,
    newMessagesBelowCount,
    onScroll,
    onNewMessagesArrived,
    scrollToLatest,
    stickToLatest,
    jumpToMessage,
  };
}
